import React, {useEffect, useState} from "react";
import {toast} from "react-toastify";
import { useTranslation } from "react-i18next";
import {
    createQuestion,
    createQuestionByExamBase,
    createQuestionVideoUpload,
    updateQuestion,
    updateQuestionByExamBase,
    uploadVideoToCloudflare
} from "../../../services/questionsApi";
import styles from "./QuestionFormModal.module.css";
import Select from "../../../components/common/select/Select";
import ForceUpdateModal from "../../../components/common/forceUpdateModal/ForceUpdateModal";
import {DifficultyLevels} from "../../../utils/difficulty.util";
import MarkdownEditor from "../../../components/common/QuestionContent/MarkdownEditor";
import TopicSelector from "../../../components/common/topicSelector/TopicSelector";
import {generateQuestionExplanation} from "../../../services/aiQuestionsApi";

// Cloudflare's basic direct-upload accepts files up to 200 MB.
const MAX_VIDEO_BYTES = 200 * 1024 * 1024;

// Mirror the backend FileUtil.validateFile rules (png/jpeg/jpg, max 5 MB) so the user is told up
// front instead of after an upload round-trip. Returns an i18n key for the violation, or null if ok.
const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg"];
const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // 5 MB — mirrors backend FileUtil.MAX_FILE_SIZE_BYTES
const validateImageFile = (file) => {
    if (!file) return null;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) return "pages.questionForm.imageInvalidType";
    if (file.size > MAX_IMAGE_BYTES) return "pages.questionForm.imageTooLarge";
    return null;
};

const QuestionFormModal = ({
                               isOpen,
                               onClose,
                               examBaseId,
                               onSuccess,
                               mode = "create",
                               initialQuestion = null,
                               subjectOptions,
                               onSubjectsChanged,
                               examSubjects = [],
                               /**
                                * Optional persistence override: `({payload, file, optionFiles, force}) => Promise`.
                                * When supplied the modal saves through this instead of the question-bank
                                * endpoints — used by the PDF import review, whose questions live in a staging
                                * table until they are committed. Keeps one editor UI (and one inline
                                * subject/topic creator) rather than a second copy that drifts.
                                */
                               onPersist = null,
                               /**
                                * Show the topic picker even when editing. The bank hides it on edit —
                                * re-filing a live question moves it out from under the exam templates
                                * that draw on its topic. Staged PDF-import questions are not in the bank
                                * yet and usually arrive with no topic at all, so they must be filed
                                * during review.
                                */
                               allowTopicChange = false
                           }) => {
    const { t } = useTranslation();
    const [text, setText] = useState("");
    const [difficultyLevel, setDifficultyLevel] = useState(null);
    const [optionCount, setOptionCount] = useState(4);
    const [options, setOptions] = useState([]);
    const [correctIndex, setCorrectIndex] = useState(0);
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [subjectId, setSubjectId] = useState("");
    const [isForceUpdateModalOpen, setIsForceUpdateModalOpen] = useState(false);
    const [forceUpdatingExamBaseMessage, setForceUpdatingExamBaseMessage] = useState(null);
    // Bumped to force-remount file <input>s so their displayed filenames clear.
    const [fileInputKey, setFileInputKey] = useState(0);
    // Video explanation (Cloudflare Stream direct upload)
    const [videoUid, setVideoUid] = useState(null);
    // Duration (seconds) of the currently-attached video — sent to backend on save so
    // the right ceil(seconds/60) of VIDEO_MINUTES_LIMIT is consumed and refundable later.
    const [videoDurationSeconds, setVideoDurationSeconds] = useState(null);
    const [existingVideoUrl, setExistingVideoUrl] = useState(null);
    const [removeVideo, setRemoveVideo] = useState(false);
    const [videoUploading, setVideoUploading] = useState(false);
    const [videoProgress, setVideoProgress] = useState(0);
    const [videoError, setVideoError] = useState("");
    // AI-generated / hand-written text explanation
    const [explanation, setExplanation] = useState("");
    const [explanationGenerating, setExplanationGenerating] = useState(false);
    const [explanationError, setExplanationError] = useState("");
    // Question type: MULTIPLE_CHOICE (option-based) or OPEN (free-text, AI-graded). Default preserves
    // the existing behaviour for every current question.
    const [questionType, setQuestionType] = useState("MULTIPLE_CHOICE");
    // OPEN questions only: the teacher's reference answer the AI grades student answers against.
    const [correctAnswer, setCorrectAnswer] = useState("");

    const resetForm = () => {
        setText("");
        setDifficultyLevel(null);
        setOptionCount(4);
        setOptions([]);
        setCorrectIndex(0);
        setFile(null);
        setSubjectId("");
        setError("");
        setLoading(false);
        setIsForceUpdateModalOpen(false);
        setForceUpdatingExamBaseMessage(null);
        setVideoUid(null);
        setVideoDurationSeconds(null);
        setExistingVideoUrl(null);
        setRemoveVideo(false);
        setVideoUploading(false);
        setVideoProgress(0);
        setVideoError("");
        setExplanation("");
        setExplanationGenerating(false);
        setExplanationError("");
        setQuestionType("MULTIPLE_CHOICE");
        setCorrectAnswer("");
        setFileInputKey((k) => k + 1);
    };

    const handleClose = () => {
        resetForm();
        onClose?.();
    };

    useEffect(() => {
        if (isOpen && mode === "edit" && initialQuestion) {
            setText(initialQuestion.text || "");
            setDifficultyLevel(initialQuestion.difficultyLevel);
            setVideoUid(initialQuestion.videoUid || null);
            setVideoDurationSeconds(initialQuestion.videoDurationSeconds || null);
            setExistingVideoUrl(initialQuestion.videoPlaybackUrl || null);
            setRemoveVideo(false);
            setVideoError("");
            setExplanation(initialQuestion.explanation || "");
            setExplanationError("");
            setQuestionType(initialQuestion.type || "MULTIPLE_CHOICE");
            setCorrectAnswer(initialQuestion.correctAnswer || "");
            // Show the topic the question is already filed under. The examSubjects default below
            // only applies to create mode, so this cannot be clobbered by it.
            setSubjectId(initialQuestion.subjectId ? String(initialQuestion.subjectId) : "");

            if (initialQuestion.options && initialQuestion.options.length > 0) {
                const mapped = initialQuestion.options.map((o) => ({
                    id: o.id ?? null,
                    optionText: o.optionText || "",
                    correct: !!o.correct,
                    file: null,
                    existingAttachmentUrl: o.attachmentUrl || null,
                    removeAttachment: false,
                }));
                setOptions(mapped);
                // Drive the visible option-row count from the question being edited,
                // otherwise it stays at the default (4) and a 5-option question shows only 4 rows.
                setOptionCount(mapped.length);
                const idx = mapped.findIndex((o) => o.correct);
                setCorrectIndex(idx === -1 ? 0 : idx);
            } else {
                setOptions([]);
                setOptionCount(4);
                setCorrectIndex(0);
            }

            setFile(null);
            setError("");
        }

        if (isOpen && mode === "create") {
            setText("");
            setDifficultyLevel(null);
            setVideoUid(null);
            setExistingVideoUrl(null);
            setRemoveVideo(false);
            setVideoError("");
            setExplanation("");
            setExplanationError("");
            setQuestionType("MULTIPLE_CHOICE");
            setCorrectAnswer("");
            setOptionCount(4);
            setOptions([]);
            setCorrectIndex(0);
            setFile(null);
            setError("");
        }
    }, [isOpen, mode, initialQuestion]);

    // Multi-topic templates: default an in-template create to the primary topic so there is always
    // a valid target subject selected.
    useEffect(() => {
        if (isOpen && mode === "create" && examBaseId && examSubjects.length > 1) {
            setSubjectId(String(examSubjects[0].id));
        }
    }, [isOpen, mode, examBaseId, examSubjects.length]);

    if (!isOpen) {
        return null;
    }

    const ensureOption = (index) => {
        if (options[index]) return options;
        const filled = [...options];
        for (let i = filled.length; i <= index; i++) {
            filled[i] = {
                id: null,
                optionText: "",
                correct: false,
                file: null,
                existingAttachmentUrl: null,
                removeAttachment: false,
            };
        }
        return filled;
    };

    const handleOptionChange = (index, value) => {
        const updated = ensureOption(index).slice();
        updated[index] = {...updated[index], optionText: value};
        setOptions(updated);
    };

    const handleCorrectChange = (index) => {
        setCorrectIndex(index);
        const base = ensureOption(index);
        const updated = base.map((o, i) => ({...o, correct: i === index}));
        setOptions(updated);
    };

    const handleFileChange = (e) => {
        const selected = e.target.files?.[0];
        const errKey = validateImageFile(selected);
        if (errKey) {
            setError(t(errKey));
            e.target.value = "";
            setFile(null);
            return;
        }
        setFile(selected || null);
    };

    // Reads the video's duration from the browser's File API (metadata only — no upload).
    // Returns seconds as an integer, or rejects if the metadata can't be parsed.
    const readVideoDuration = (file) => new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const v = document.createElement("video");
        v.preload = "metadata";
        v.onloadedmetadata = () => {
            URL.revokeObjectURL(url);
            const seconds = Math.round(v.duration);
            if (!Number.isFinite(seconds) || seconds <= 0) {
                reject(new Error("invalid"));
            } else {
                resolve(seconds);
            }
        };
        v.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error("metadata"));
        };
        v.src = url;
    });

    const handleVideoFileChange = async (e) => {
        const selected = e.target.files?.[0];
        if (!selected) return;
        setVideoError("");
        if (!selected.type.startsWith("video/")) {
            setVideoError(t("pages.questionForm.videoInvalidType"));
            return;
        }
        if (selected.size > MAX_VIDEO_BYTES) {
            setVideoError(t("pages.questionForm.videoTooLarge"));
            return;
        }
        // Read duration via <video> metadata BEFORE uploading — saves a wasted upload
        // when the file would be rejected by Cloudflare's server-side cap (5 min).
        let durationSeconds;
        try {
            durationSeconds = await readVideoDuration(selected);
        } catch {
            setVideoError(t("pages.questionForm.videoDurationUnreadable"));
            return;
        }
        if (durationSeconds > 300) {
            setVideoError(t("pages.questionForm.videoTooLong"));
            return;
        }
        try {
            setVideoUploading(true);
            setVideoProgress(0);
            const {uid, uploadUrl} = await createQuestionVideoUpload();
            await uploadVideoToCloudflare(uploadUrl, selected, setVideoProgress);
            setVideoUid(uid);
            setVideoDurationSeconds(durationSeconds);
            setExistingVideoUrl(null);
            setRemoveVideo(false);
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.message ||
                t("pages.questionForm.videoUploadFailed");
            setVideoError(msg);
        } finally {
            setVideoUploading(false);
        }
    };

    const handleRemoveVideo = () => {
        setVideoUid(null);
        setVideoDurationSeconds(null);
        setExistingVideoUrl(null);
        setRemoveVideo(true);
        setVideoError("");
    };

    const handleGenerateExplanation = async () => {
        setExplanationError("");
        const trimmedText = text.trim();
        if (!trimmedText) {
            setExplanationError(t("pages.questionForm.questionTextRequired"));
            return;
        }
        const opts = options
            .slice(0, optionCount)
            .map((o, i) => ({
                optionText: (o?.optionText || "").trim(),
                correct: i === correctIndex,
            }))
            .filter((o) => o.optionText !== "");
        try {
            setExplanationGenerating(true);
            const lang = localStorage.getItem("gredus.language") || "az";
            const data = await generateQuestionExplanation({text: trimmedText, options: opts, language: lang});
            setExplanation(data?.explanation || "");
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.message ||
                t("pages.questionForm.explanationGenerateFailed");
            setExplanationError(msg);
        } finally {
            setExplanationGenerating(false);
        }
    };

    const handleOptionFileChange = (index, e) => {
        const selected = e.target.files?.[0] || null;
        const errKey = validateImageFile(selected);
        if (errKey) {
            setError(t(errKey));
            e.target.value = "";
            return;
        }
        const updated = ensureOption(index).slice();
        updated[index] = {
            ...updated[index],
            file: selected,
            removeAttachment: false,
        };
        setOptions(updated);
    };

    const handleRemoveOptionAttachment = (index) => {
        const updated = ensureOption(index).slice();
        updated[index] = {
            ...updated[index],
            file: null,
            existingAttachmentUrl: null,
            removeAttachment: true,
        };
        setOptions(updated);
    };

    const handleSubmit = async (e, force) => {
        e.preventDefault();
        setError("");

        if (videoUploading) {
            setError(t("pages.questionForm.uploadingVideo"));
            return;
        }

        if (explanationGenerating) {
            setError(t("pages.questionForm.explanationGenerating"));
            return;
        }

        if (!examBaseId && mode !== "edit" && (subjectId === undefined || subjectId === null || subjectId === '')) {
            setError(t("pages.questionForm.topicRequired"));
            return;
        }

        const trimmedText = text.trim();
        if (!trimmedText) {
            setError(t("pages.questionForm.questionTextRequired"));
            return;
        }

        if (difficultyLevel === undefined || difficultyLevel === null || difficultyLevel.trim() === '') {
            setError(t("pages.questionForm.difficultyRequired"));
            return;
        }

        const isOpenType = questionType === "OPEN";

        // Options are built/validated only for option-based questions. An OPEN question carries no
        // options and instead requires a single free-text reference answer.
        let cleanedOptions = [];
        const optionFiles = {};
        let trimmedCorrectAnswer = "";

        if (isOpenType) {
            trimmedCorrectAnswer = (correctAnswer || "").trim();
            if (!trimmedCorrectAnswer) {
                setError(t("pages.questionForm.correctAnswerRequired"));
                return;
            }
        } else {
            // Keep original index so we can align uploaded files; an option counts if it has
            // either text or an image (newly picked file or preserved existing attachment).
            const cleanedOptionsWithIdx = options
                .slice(0, optionCount)
                .map((o, index) => ({
                    origIndex: index,
                    id: o?.id ?? null,
                    optionText: (o?.optionText || "").trim(),
                    correct: index === correctIndex,
                    file: o?.file || null,
                    hasExistingImage: !!(o?.existingAttachmentUrl) && !o?.removeAttachment,
                    removeAttachment: !!o?.removeAttachment,
                }))
                .filter((o) => o.optionText !== "" || o.file || o.hasExistingImage);

            cleanedOptions = cleanedOptionsWithIdx.map(
                ({origIndex, file, hasExistingImage, ...rest}) => rest
            );

            // After filtering, payload index changes. Build optionFiles map keyed by NEW index.
            cleanedOptionsWithIdx.forEach((o, newIdx) => {
                if (o.file) optionFiles[newIdx] = o.file;
            });

            if (cleanedOptions.length <= 1) {
                setError(t("pages.questionForm.minTwoOptions"));
                return;
            }

            const expectedCount = parseInt(optionCount, 10);
            if (Number.isFinite(expectedCount) && cleanedOptions.length < expectedCount) {
                setError(t("pages.questionForm.fillAllOptions", { count: expectedCount }));
                return;
            }

            if (correctIndex < 0 || correctIndex >= options.length) {
                setError(t("pages.questionForm.selectCorrect"));
                return;
            }

            const correctOpt = options[correctIndex];
            const correctHasText = !!correctOpt && !!(correctOpt.optionText || "").trim();
            const correctHasImage = !!correctOpt && (correctOpt.file || (correctOpt.existingAttachmentUrl && !correctOpt.removeAttachment));
            if (!correctOpt || (!correctHasText && !correctHasImage)) {
                setError(t("pages.questionForm.correctRequired"));
                return;
            }
        }

        const payload = {
            text: trimmedText,
            difficultyLevel: difficultyLevel,
            type: questionType,
            options: isOpenType ? [] : cleanedOptions,
            correctAnswer: isOpenType ? trimmedCorrectAnswer : null,
            subjectId: subjectId ? Number(subjectId) : null,
        };

        if (mode === "edit" && initialQuestion?.id) {
            payload.id = initialQuestion.id;
        }

        if (videoUid) {
            payload.videoUid = videoUid;
            // Backend will consume ceil(seconds/60) from VIDEO_MINUTES_LIMIT on save.
            if (videoDurationSeconds) {
                payload.videoDurationSeconds = videoDurationSeconds;
            }
        }
        if (mode === "edit" && removeVideo) {
            payload.removeVideo = true;
        }
        payload.explanation = (explanation && explanation.trim()) ? explanation.trim() : null;

        try {
            setLoading(true);
            if (onPersist) {
                // Caller owns persistence (e.g. staged PDF-import questions, which are not bank
                // questions yet). Everything above — validation, options, images, topic creation —
                // is identical, so only the write differs.
                await onPersist({ payload, file, optionFiles, force });
                toast.success(t("pages.questionForm.updateSuccess"));
                setIsForceUpdateModalOpen(false);
            } else if (mode === "edit") {
                examBaseId
                    ? await updateQuestionByExamBase(examBaseId, payload, file, optionFiles, force)
                    : await updateQuestion(payload, file, optionFiles, force);
                toast.success(t("pages.questionForm.updateSuccess"));
                setIsForceUpdateModalOpen(false);
            } else {
                examBaseId
                    ? await createQuestionByExamBase(examBaseId, payload, file, optionFiles)
                    : await createQuestion(payload, file, optionFiles);
                toast.success(t("pages.questionForm.createSuccess"));
            }
            if (onSuccess) {
                // On create, hand back the topic the question was filed under so the bank list can
                // auto-filter to it (the wizard / other callers simply ignore the argument).
                onSuccess(mode === "create" ? payload.subjectId : undefined);
            }
            resetForm();
            onClose?.();
        } catch (e) {
            if (e.status === 409) {
                setIsForceUpdateModalOpen(true)
                setForceUpdatingExamBaseMessage(e?.response?.data?.message)
                return
            }
            const msg =
                e?.response?.data?.message ||
                e?.response?.data?.error ||
                e.message ||
                t("pages.questionForm.operationFailed");
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseForceUpdateModal = () => {
        setIsForceUpdateModalOpen(false);
    };

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <header className={styles.header}>
                    <div className={styles.titleWrap}>
                        <span className={styles.titleIcon} aria-hidden>{mode === "edit" ? "✏️" : "📝"}</span>
                        <h2 className={styles.title}>{mode === "edit" ? t("pages.questionForm.editTitle") : t("pages.questionForm.createTitle")}</h2>
                    </div>
                    <button type="button" className={styles.closeBtn} onClick={handleClose} aria-label={t("pages.questionForm.closeAria")}>
                        <span className={styles.closeIcon} aria-hidden>×</span>
                    </button>
                </header>

                <div className={styles.body}>
                    {error && (
                        <div className={styles.error} role="alert">
                            <span className={styles.errorIcon}>!</span>
                            <span>{error}</span>
                        </div>
                    )}

                    <form onSubmit={(e) => handleSubmit(e, false)} className={styles.form}>
                        <div className={styles.formCard}>
                            {
                                (allowTopicChange || (!examBaseId && mode !== "edit")) &&
                                <div className={styles.field}>
                                    <TopicSelector
                                        value={subjectId}
                                        onChange={(id) => setSubjectId(id ? String(id) : "")}
                                        topics={subjectOptions}
                                        onTopicCreated={(newTopic) => onSubjectsChanged?.(newTopic)}
                                        placeholder={t("pages.questionForm.selectTopic")}
                                    />
                                </div>
                            }
                            {
                                !!examBaseId && mode === "create" && examSubjects.length > 1 &&
                                <div className={styles.field}>
                                    <label className={styles.label} htmlFor="question-target-subject">{t("pages.questionForm.targetSubject")}</label>
                                    <Select
                                        id="question-target-subject"
                                        className={styles.filterInput}
                                        value={subjectId}
                                        onChange={(e) => setSubjectId(e.target.value)}
                                        options={examSubjects.map((s) => ({
                                            value: s.id,
                                            label: s.parentName ? `${s.parentName} › ${s.title}` : s.title,
                                        }))}
                                    />
                                </div>
                            }
                            <div className={styles.field}>
                                <label className={styles.label} htmlFor="question-text">{t("pages.questionForm.questionText")}</label>
                                <MarkdownEditor
                                    value={text}
                                    onChange={setText}
                                    placeholder={t("pages.questionForm.questionTextPlaceholder")}
                                    showPreview={true}
                                    showLangShortcuts={true}
                                    minRows={6}
                                />
                            </div>

                            <div className={styles.field}>
                                <label className={styles.label} htmlFor="question-type">{t("pages.questionForm.questionType")}</label>
                                <Select
                                    id="question-type"
                                    className={styles.filterInput}
                                    value={questionType}
                                    onChange={(e) => setQuestionType(e.target.value)}
                                    options={[
                                        { value: "MULTIPLE_CHOICE", label: t("questionType.MULTIPLE_CHOICE") },
                                        { value: "OPEN", label: t("questionType.OPEN") },
                                    ]}
                                />
                            </div>

                            <div className={styles.row}>
                                <div className={styles.field}>
                                    <label className={styles.label} htmlFor="difficulty-level">{t("pages.questionForm.difficulty")}</label>
                                    <Select
                                        id="difficulty-level"
                                        className={styles.filterInput}
                                        value={difficultyLevel}
                                        onChange={(e) => setDifficultyLevel(e.target.value)}
                                        options={[
                                            { value: "", label: t("pages.questionForm.select") },
                                            ...Object.values(DifficultyLevels).map((s) => ({
                                                value: s.name,
                                                label: s.label,
                                            })),
                                        ]}
                                    />
                                </div>
                                {questionType !== "OPEN" && (
                                    <div className={styles.field}>
                                        <label className={styles.label} htmlFor="option-count">{t("pages.questionForm.optionCount")}</label>
                                        <input
                                            id="option-count"
                                            type="number"
                                            className={styles.input}
                                            value={optionCount}
                                            min={2}
                                            max={5}
                                            onChange={(e) => setOptionCount(e.target.value)}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className={styles.field}>
                                <label className={styles.label} htmlFor="question-file">
                                    {t("pages.questionForm.image")}
                                    <span style={{ fontWeight: 400, color: "#94a3b8", fontSize: 12, marginLeft: 6 }}>
                                        {t("pages.questionForm.imageSizeHint")}
                                    </span>
                                </label>
                                <input
                                    key={`q-file-${fileInputKey}`}
                                    id="question-file"
                                    type="file"
                                    accept="image/png,image/jpeg,image/jpg"
                                    className={styles.fileInput}
                                    onChange={handleFileChange}
                                />
                            </div>

                            <div className={styles.field}>
                                <label className={styles.label} htmlFor="question-video">{t("pages.questionForm.videoExplanation")}</label>
                                {videoUid && !removeVideo ? (
                                    <div className={styles.videoAttachedRow}>
                                        <span className={styles.videoAttachedText}>✓ {t("pages.questionForm.videoAttached")}</span>
                                        {existingVideoUrl && (
                                            <a
                                                href={existingVideoUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                className={styles.videoPreviewLink}
                                            >
                                                {t("pages.questionForm.videoWatch")}
                                            </a>
                                        )}
                                        <button
                                            type="button"
                                            onClick={handleRemoveVideo}
                                            className={styles.secondaryBtn}
                                            style={{padding: "6px 12px", fontSize: 13}}
                                        >
                                            {t("pages.questionForm.removeVideo")}
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <input
                                            key={`q-video-${fileInputKey}`}
                                            id="question-video"
                                            type="file"
                                            accept="video/*"
                                            className={styles.fileInput}
                                            onChange={handleVideoFileChange}
                                            disabled={videoUploading}
                                        />
                                        {videoUploading && (
                                            <div className={styles.videoProgress}>
                                                <div className={styles.videoProgressBar}>
                                                    <div
                                                        className={styles.videoProgressFill}
                                                        style={{width: `${videoProgress}%`}}
                                                    />
                                                </div>
                                                <span className={styles.videoProgressText}>
                                                    {t("pages.questionForm.uploadingVideo")} {videoProgress}%
                                                </span>
                                            </div>
                                        )}
                                        {videoError && <div className={styles.videoErrorText}>{videoError}</div>}
                                    </>
                                )}
                            </div>

                            <div className={styles.field}>
                                <div className={styles.explanationHeader}>
                                    <label className={styles.label} htmlFor="question-explanation">{t("pages.questionForm.explanation")}</label>
                                    <button
                                        type="button"
                                        className={styles.aiGenerateBtn}
                                        onClick={handleGenerateExplanation}
                                        disabled={explanationGenerating}
                                    >
                                        {explanationGenerating ? t("pages.questionForm.explanationGenerating") : t("pages.questionForm.generateWithAi")}
                                    </button>
                                </div>
                                <MarkdownEditor
                                    value={explanation}
                                    onChange={setExplanation}
                                    placeholder={t("pages.questionForm.explanationPlaceholder")}
                                    showPreview={true}
                                    minRows={4}
                                />
                                {explanationError && <div className={styles.videoErrorText}>{explanationError}</div>}
                            </div>
                        </div>

                        {questionType === "OPEN" ? (
                            <div className={styles.optionsSection}>
                                <h3 className={styles.sectionTitle}>{t("pages.questionForm.correctAnswer")}</h3>
                                <div className={styles.field}>
                                    <MarkdownEditor
                                        value={correctAnswer}
                                        onChange={setCorrectAnswer}
                                        placeholder={t("pages.questionForm.correctAnswerPlaceholder")}
                                        showPreview={false}
                                        showLangShortcuts={false}
                                        minRows={3}
                                    />
                                    <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 6 }}>{t("pages.questionForm.correctAnswerHint")}</p>
                                </div>
                            </div>
                        ) : (
                        <div className={styles.optionsSection}>
                            <h3 className={styles.sectionTitle}>{t("pages.questionForm.options")}</h3>
                            <div className={styles.optionsGroup}>
                                {Array.from({length: optionCount}, (_, index) => {
                                    const opt = options[index] || {};
                                    const previewUrl = opt.file
                                        ? URL.createObjectURL(opt.file)
                                        : (opt.existingAttachmentUrl || null);
                                    return (
                                        <div key={index} className={styles.optionRow}>
                                            <span className={styles.optionLetter}>
                                              {String.fromCharCode(65 + index)}
                                            </span>

                                            <div className={styles.optionEditorWrap}>
                                                <MarkdownEditor
                                                    value={opt.optionText || ""}
                                                    onChange={(val) => handleOptionChange(index, val)}
                                                    placeholder={t("pages.questionForm.optionPlaceholder", { index: index + 1 })}
                                                    showPreview={false}
                                                    showLangShortcuts={false}
                                                    minRows={2}
                                                />

                                                <div style={{display: "flex", alignItems: "center", gap: 8, marginTop: 6, flexWrap: "wrap"}}>
                                                    <input
                                                        key={`opt-file-${index}-${fileInputKey}`}
                                                        type="file"
                                                        accept="image/png,image/jpeg,image/jpg"
                                                        className={styles.fileInput}
                                                        onChange={(e) => handleOptionFileChange(index, e)}
                                                    />
                                                    {previewUrl && (
                                                        <>
                                                            <img
                                                                src={previewUrl}
                                                                alt={t("pages.questionForm.optionImageAlt", { index: index + 1 })}
                                                                style={{maxHeight: 60, borderRadius: 2}}
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveOptionAttachment(index)}
                                                                className={styles.secondaryBtn}
                                                                style={{padding: "4px 8px", fontSize: 12}}
                                                            >
                                                                {t("pages.questionForm.removeImage")}
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            <label className={styles.radioWrap}>
                                                <input
                                                    type="radio"
                                                    name="correctOption"
                                                    checked={correctIndex === index}
                                                    onChange={() => handleCorrectChange(index)}
                                                    className={styles.radio}
                                                />
                                                <span className={styles.radioLabel}>{t("pages.questionForm.correct")}</span>
                                            </label>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        )}

                        <div className={styles.actions}>
                            <button type="button" className={styles.secondaryBtn} onClick={handleClose} disabled={loading}>
                                {t("common.cancel")}
                            </button>
                            <button type="submit" className={styles.primaryBtn} disabled={loading || videoUploading || explanationGenerating}>
                                {loading ? (
                                    <>
                                        <span className={styles.btnSpinner} aria-hidden/>
                                        <span>{t("pages.questionForm.loading")}</span>
                                    </>
                                ) : mode === "edit" ? (
                                    t("pages.questionForm.updateBtn")
                                ) : (
                                    t("pages.questionForm.createBtn")
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
            <ForceUpdateModal
                isOpen={isForceUpdateModalOpen}
                onConfirm={(e) => handleSubmit(e, true)}
                onCancel={handleCloseForceUpdateModal}
                message={forceUpdatingExamBaseMessage}
                disabled={loading}
            />
        </div>
    );
};

export default QuestionFormModal;
